import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { finalize } from 'rxjs/operators'

import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { ChangeRequestActions } from '@app/features/scope-overview/change-request/store/actions/change-request.actions'
import { ExchangeRate } from '@app/features/scope-overview/model/exchange-rate.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { PipesModule } from '@shared/pipe/index.module'
import { ScopeOverviewService } from '@app/features/scope-overview/service/scope-overview.service'
import { ScopeRetainedHoursStats } from '@app/features/scope-overview/model/scope-retained-hours-stats.model'
import { SowRetainedHoursStats } from '@app/features/scope-overview/model/sow-retained-hours-stats.model'
import { ChangeRequest } from '@core/model/change-request.model'
import { ApplicationSetting } from '@core/model/enums/application-setting.enum'
import { ScopeVersion } from '@core/model/scope-version'
import { Store } from '@ngrx/store'
import { ScopeUiDropdownComponent } from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component'
import { ExchangeRateService } from '@shared/services/exchange-rate.service'
import { SharedModule } from '@shared/shared.module'
import { isChangeRequestEnabled } from '@app/features/scope-overview/utils/is-change-request-enabled.utils'
import { AuthService } from '@app/core/service/auth.service'

@Component({
  selector: 'details-overview',
  standalone: true,
  imports: [CommonModule, PipesModule, FormsModule, ScopeUiDropdownComponent, SharedModule],
  templateUrl: './details-overview.component.html',
})
export class DetailsOverviewComponent implements OnChanges {
  expandedMode = false
  versionNumbers: number[] = []
  selectedVersion: number
  selectedScopeId: number
  selectedExchangeRate: ExchangeRate
  readonly NONE_SELECTED = { id: null, localId: null, isNoneOption: true, name: 'None' } as any

  @Input() scopeVersion!: ScopeVersion
  @Input() scopeRetainedHoursStats!: ScopeRetainedHoursStats
  @Input() sowRetainedHoursStats!: SowRetainedHoursStats
  @Input() trafficMetadataLookupFields!: any
  @Input() thirdPartyCostBalance!: Money
  @Input() exchangeRates!: ExchangeRate[]
  @Input() currentChangeRequest?: ChangeRequest
  @Input() changeRequests: ChangeRequest[] = []
  @Input() hasChangeRequestsFeature = false

  @Output() onVersionChange = new EventEmitter<number>()
  @Output() onChangeRequestChange = new EventEmitter<ChangeRequest | null>()

  get selectedChangeRequest(): ChangeRequest {
    return this.currentChangeRequest || this.NONE_SELECTED
  }

  private readonly CURRENCY_CONVERSION_KEY = 'scope_internal_currency_conversion'
  private isCurrencyUpdating = false

  constructor(
    private store$: Store,
    public exchangeRateService: ExchangeRateService,
    private scopeOverviewService: ScopeOverviewService,
    private cdr: ChangeDetectorRef,
    protected authService: AuthService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.generateVersionNumbers()
    this.selectedVersion = this.scopeVersion?.version

    if (changes.scopeVersion && this.scopeVersion && this.selectedScopeId != this.scopeVersion?.identity?.id)
      this.store$.dispatch(ChangeRequestActions.getChangeRequests())

    this.selectedScopeId = this.scopeVersion?.identity?.id
    if (this.scopeVersion && (changes.scopeVersion || changes.exchangeRates)) {
      this.loadSavedCurrency()
    }
  }

  changeVersion() {
    this.onVersionChange.emit(this.selectedVersion)
  }

  changeCurrency() {
    this.exchangeRateService.exchangeRate = this.selectedExchangeRate

    if (!this.scopeVersion?.identity?.id || !this.selectedExchangeRate?.toCurrencyCode) return

    this.isCurrencyUpdating = true

    this.scopeOverviewService
      .updateCurrency(this.scopeVersion.identity.id, this.selectedExchangeRate.toCurrencyCode)
      .pipe(finalize(() => (this.isCurrencyUpdating = false)))
      .subscribe((updatedScope) => {
        if (this.scopeVersion) {
          this.scopeVersion.dynamicFields = updatedScope.dynamicFields
        }
      })
  }

  private loadSavedCurrency(): void {
    if (this.isCurrencyUpdating) return

    const savedCurrencyCode = this.scopeVersion?.dynamicFields?.[this.CURRENCY_CONVERSION_KEY]
    if (!savedCurrencyCode) return

    const savedExchangeRate = this.exchangeRates?.find((rate) => rate.toCurrencyCode === savedCurrencyCode)

    if (savedExchangeRate) {
      this.selectedExchangeRate = savedExchangeRate
      this.exchangeRateService.exchangeRate = savedExchangeRate
      this.cdr.detectChanges()
    }
  }

  changeChangeRequest(changeRequest: ChangeRequest) {
    this.onChangeRequestChange.emit(this.isNoneSelected(changeRequest) ? null : changeRequest)
  }

  versionDisplayFn = (version: number) => version.toString()
  exchangeRateDisplayFn = (exchangeRate: ExchangeRate) => exchangeRate.toCurrencyCode
  changeRequestDisplayFn = (changeRequest: ChangeRequest) =>
    changeRequest.getChangeRequestId
      ? `${changeRequest.name} (${changeRequest.getChangeRequestId()})`
      : changeRequest.name

  getChangeRequestOptions(): any[] {
    return [this.NONE_SELECTED, ...(this.changeRequests || [])]
  }

  generateVersionNumbers() {
    const currentVersionNumber = this.scopeVersion.identity?.currentVersionNumber
    this.versionNumbers = Array.from({ length: currentVersionNumber }, (_, i) => i + 1)
  }

  private isNoneSelected(changeRequest: any): boolean {
    return !changeRequest || changeRequest.isNoneOption === true
  }

  getExternalDropdownDynamicFieldValue = (setting: any) => {
    if (typeof setting === 'object' && setting !== null) {
      return (
        setting?.Name ||
        setting?.Id ||
        setting?.name ||
        setting?.entityId ||
        setting?.ProjectName ||
        setting?.ProjectNumber
      )
    }
    return setting || '--'
  }

  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly isChangeRequestEnabled = isChangeRequestEnabled
}
